import { X } from '@phosphor-icons/react';
import { HttpStatusCode } from 'axios';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { ClipLoader } from 'react-spinners';
import { AccentButton, NeutralButton } from '../../../../../../shared/Buttons';
import { Entity, PatchVendorRequest } from '../../../../../../types/apiTypes';
import useApi from '../../../../../../utils/AxiosWrapper';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';
import { useUser } from '../../../../../../utils/UserContext';
import { HoursOfOperation } from '../../../../../signup/vendors/VendorBusinessDetails';
import dayjs from 'dayjs';
import HoursOfOperationWidget from '../../../../../signup/vendors/widgets/HoursOfOperationsWidget';

const defaultHoursOfOperation = {
  Monday: {
    isOpen: true,
    from: dayjs('09:00 AM', 'hh:mm A').format('hh:mm A'),
    to: dayjs('05:00 PM', 'hh:mm A').format('hh:mm A'),
  },
  Tuesday: {
    isOpen: true,
    from: dayjs('09:00 AM', 'hh:mm A').format('hh:mm A'),
    to: dayjs('05:00 PM', 'hh:mm A').format('hh:mm A'),
  },
  Wednesday: {
    isOpen: true,
    from: dayjs('09:00 AM', 'hh:mm A').format('hh:mm A'),
    to: dayjs('05:00 PM', 'hh:mm A').format('hh:mm A'),
  },
  Thursday: {
    isOpen: true,
    from: dayjs('09:00 AM', 'hh:mm A').format('hh:mm A'),
    to: dayjs('05:00 PM', 'hh:mm A').format('hh:mm A'),
  },
  Friday: {
    isOpen: true,
    from: dayjs('09:00 AM', 'hh:mm A').format('hh:mm A'),
    to: dayjs('05:00 PM', 'hh:mm A').format('hh:mm A'),
  },
  Saturday: { isOpen: false, from: '', to: '' },
  Sunday: { isOpen: false, from: '', to: '' },
};
type FormData = {
  businessPhone: string;
  hoursOfOperation: {
    Monday: HoursOfOperation;
    Tuesday: HoursOfOperation;
    Wednesday: HoursOfOperation;
    Thursday: HoursOfOperation;
    Friday: HoursOfOperation;
    Saturday: HoursOfOperation;
    Sunday: HoursOfOperation;
  };
  websiteUrl: string;
  descriptionOfServices: string;
};

export const EditBusinessInfoModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { register, handleSubmit, control, watch, reset } = useForm<FormData>({
    defaultValues: {
      businessPhone: '',
      hoursOfOperation: defaultHoursOfOperation,
      websiteUrl: '',
      descriptionOfServices: '',
    },
  });
  const { userRecord, userId } = useUser();
  const { callProtectedApi } = useApi();
  const { screenView } = useScreenView();
  const hoursOfOperation = watch('hoursOfOperation');
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (userRecord?.userType === Entity.Vendor) {
      reset({
        businessPhone: userRecord?.business_phone_number,
        hoursOfOperation:
          JSON.parse(userRecord?.hours_of_operation ?? '') ??
          defaultHoursOfOperation,
        websiteUrl: userRecord?.website_url,
        descriptionOfServices: userRecord?.description_of_services,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRecord]);
  const onSubmit = (data: FormData) => {
    updateVendorInfo(userId ?? '', data);
    // Handle form submission logic here
  };

  const updateVendorInfo = async (vendor_id: string, recordInfo: FormData) => {
    setLoading(true);
    try {
      const response = await callProtectedApi(
        '/vendors/' + vendor_id,
        'patch',
        {
          business_phone_number: recordInfo?.businessPhone,
          hours_of_operation: JSON.stringify(recordInfo?.hoursOfOperation),
          website_url: recordInfo?.websiteUrl,
          description_of_services: recordInfo?.descriptionOfServices,
        } as PatchVendorRequest,
        'Error occurred while updating vendor profile.'
      );
      const data = response?.data?.data?.vendor;
      if (
        data &&
        Object.entries(data)?.length > 0 &&
        response?.status === HttpStatusCode.Ok
      ) {
        onClose();
        window.location.reload();
        toast.success('Account info updated successfully.');
      } else {
        toast.error('Error occurred while updating account info.');
      }
    } catch (e: any) {
      console.error('Error occurred while updating vendor info: ', e);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900 bg-opacity-50">
            <div className="h-auto max-h-[90%] w-full overflow-x-hidden rounded-[4px] bg-white sm:max-w-[95%] md:max-w-[80%] lg:max-w-[60%]">
              <div className="flex h-[56px] w-full flex-row items-center justify-between p-4 align-middle">
                <h2 className="text-[16px] font-[600] text-primary">
                  Update Business Info
                </h2>
                <X
                  className="hover:cursor-pointer hover:opacity-80"
                  onClick={onClose}
                  weight="regular"
                  size={24}
                />
              </div>
              <form
                className="flex h-full w-full flex-col gap-3 overflow-y-auto border-[1px] border-bordergray p-6"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Business Phone Number
                  </label>
                  <input
                    type="text"
                    {...register('businessPhone', {})}
                    placeholder="Enter phone number"
                    className="w-full rounded-md border border-gray-300 p-2 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Website URL
                  </label>
                  <input
                    type="text"
                    {...register('websiteUrl', {})}
                    placeholder="Enter your website URL"
                    className="w-full rounded-md border border-gray-300 p-2 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Description of Services
                  </label>
                  <input
                    type="text"
                    {...register('descriptionOfServices', {})}
                    placeholder="Enter description"
                    className="w-full rounded-md border border-gray-300 p-2 focus:outline-none"
                  />
                </div>
                <HoursOfOperationWidget
                  control={control}
                  hoursOfOperation={hoursOfOperation}
                />
              </form>
              {/* Buttons outside the form */}
              <div className="flex justify-between space-x-2 p-4">
                <NeutralButton
                  onClick={onClose}
                  addClass="rounded-full w-[238px] h-[48px]"
                >
                  Cancel
                </NeutralButton>
                <AccentButton
                  submit={true}
                  onClick={handleSubmit(onSubmit)} // Use handleSubmit to trigger form submission
                  addClass="rounded-full hover:bg-yellow-600 w-[238px] h-[48px]"
                >
                  {!loading ? (
                    'Update'
                  ) : (
                    <ClipLoader
                      color="#1D213B"
                      loading={loading}
                      size={20}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                      className="h-full w-full justify-center"
                    />
                  )}
                </AccentButton>
              </div>
            </div>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return <>{renderScreenView(screenView)}</>;
};

export default EditBusinessInfoModal;
